import {
  AUTISM_FOLLOW_THROUGH_OPTIONS,
  AUTISM_OTHER_FACTOR_GROUPS,
  AUTISM_PRIORITY_PLANS_OPTIONS,
} from '../constants/autismTriageOutcomes';

/**
 * Mechanical pull-through of the unmet needs a clinician has recorded during
 * the autism triage wizard (LSC-042 Sections 11 and 12).
 *
 * "Unmet need" is defined purely by what was recorded — never inferred:
 *   - a support factor whose follow-through status was set to anything other
 *     than "Completed and in place";
 *   - risks recorded as present and needing reduction (step 10);
 *   - unresolved priority problems recorded as present (step 10), with the
 *     recorded plans-in-place answer.
 *
 * The list is decision support only: it surfaces the clinician's own answers
 * so the Conclusion and Signposting steps never rely on memory. It performs
 * no scoring, weighting or interpretation.
 */

const FOLLOW_THROUGH_LABELS = Object.fromEntries(
  AUTISM_FOLLOW_THROUGH_OPTIONS.map((o) => [o.id, o.label]),
);

const PRIORITY_PLANS_LABELS = Object.fromEntries(
  AUTISM_PRIORITY_PLANS_OPTIONS.map((o) => [o.id, o.label]),
);

const FACTOR_LABELS = Object.fromEntries(
  AUTISM_OTHER_FACTOR_GROUPS.flatMap((group) =>
    group.factors.map((factor) => [factor.factorKey, factor.label]),
  ),
);

/**
 * @param {Object} recorded
 * @param {Object} [recorded.followThrough] — { [factorKey]: statusId } as held
 *   by the wizard (only statuses the clinician explicitly set).
 * @param {boolean|null} [recorded.riskPresent]
 * @param {string} [recorded.riskDetail]
 * @param {boolean|null} [recorded.priorityPresent]
 * @param {string|null} [recorded.priorityPlans]
 * @param {string} [recorded.priorityDetail]
 * @returns {Array<{ key: string, label: string, detail: string }>} in a stable
 *   order: support gaps (wizard factor order), then risks, then priority
 *   problems. Empty when nothing recorded qualifies.
 */
export function collectRecordedUnmetNeeds({
  followThrough = {},
  riskPresent = null,
  riskDetail = '',
  priorityPresent = null,
  priorityPlans = null,
  priorityDetail = '',
} = {}) {
  const needs = [];

  AUTISM_OTHER_FACTOR_GROUPS.forEach((group) => {
    group.factors.forEach((factor) => {
      if (!factor.supportsFollowThrough) return;
      const status = followThrough[factor.factorKey];
      if (!status || status === 'completed_in_place') return;
      needs.push({
        key: `followThrough:${factor.factorKey}`,
        label: FACTOR_LABELS[factor.factorKey] || factor.factorKey,
        detail: `Follow-through recorded as: ${FOLLOW_THROUGH_LABELS[status] || status}`,
      });
    });
  });

  if (riskPresent === true) {
    needs.push({
      key: 'risks',
      label: 'Risks recorded as needing reduction first',
      detail: (riskDetail || '').trim(),
    });
  }

  if (priorityPresent === true) {
    const plansLabel = priorityPlans
      ? `Plans in place: ${PRIORITY_PLANS_LABELS[priorityPlans] || priorityPlans}`
      : 'Plans in place: not recorded';
    const detail = (priorityDetail || '').trim();
    needs.push({
      key: 'priorityProblems',
      label: 'Unresolved priority problems recorded',
      detail: detail ? `${plansLabel}. ${detail}` : plansLabel,
    });
  }

  return needs;
}
